import {
	type GuestSignatureStatus,
	USER_AGREEMENT_DOCUMENT_KEYS,
	guestSignatureStatusQueryOptions,
	signDocument,
	useDocumentToBeSigned,
} from "@/api/user-agreement-document";
import { static_errors } from "@/axios/constants";
import { Button, buttonVariants } from "@/components/ui/button/button";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import Spinner from "@/components/ui/spinner";
import Typography from "@/components/ui/typography";
import { useDocumentSigningEvents } from "@/features/user-agreement/useDocumentSigningEvents";
import { queryClient } from "@/query-client";
import { useMutation } from "@tanstack/react-query";
import { Link, useLoaderData, useRouter } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import PageLoader from "../ui/PageLoader";
import { PdfViewer } from "./PdfViewer";
import { UserAgreementSigning } from "./UserAgreementSignatureDrawer";

type AgreementView = "main" | "document";

interface AgreementFlowProps {
	locationId: number;
}

export function AgreementFlow({ locationId }: AgreementFlowProps) {
	const [view, setView] = useState<AgreementView>("main");

	const documentQuery = useDocumentToBeSigned(locationId);
	const document = documentQuery.data;
	const pdfLoadError = documentQuery.isError
		? "Failed to load document."
		: null;
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const router = useRouter();
	const { trackEvent } = useDocumentSigningEvents(locationId);

	const { eventTypes } = useLoaderData({
		from: "/main/deposit",
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount, cleanup on unmount only
	useEffect(() => {
		trackEvent(eventTypes.page_landed);
		return () => {
			trackEvent(eventTypes.page_exited);
		};
	}, []);

	const signDocumentMutation = useMutation({
		mutationFn: signDocument,
		onSuccess: (_data, variables) => {
			trackEvent(eventTypes.document_signed);

			queryClient.invalidateQueries(
				guestSignatureStatusQueryOptions(variables.locationId),
			);
			queryClient.setQueryData<GuestSignatureStatus>(
				USER_AGREEMENT_DOCUMENT_KEYS.guestSignatureStatus(variables.locationId),
				{ signed: true },
			);
		},
	});

	const signSucceeded = signDocumentMutation.isSuccess;

	if (view === "document") {
		return (
			<div className="flex flex-col p-6 w-full h-dvh max-h-dvh">
				<button type="button" onClick={() => setView("main")}>
					<ArrowLeft className="text-textDark -top-2 self-start" />
				</button>
				<div className="flex flex-1 min-h-0 flex-col mt-4 rounded-lg border border-input overflow-hidden">
					{pdfLoadError && (
						<div
							className="flex items-center gap-2 px-3 py-2 text-sm text-destructive bg-destructive/10"
							role="alert"
						>
							<AlertCircle className="w-4 h-4 flex-shrink-0" />
							<span>{pdfLoadError}</span>
						</div>
					)}
					{documentQuery.isPending || (!document?.blob && !pdfLoadError) ? (
						<div className="flex flex-1 flex-col items-center justify-center gap-3 py-12">
							<Spinner className="w-10 h-10 fill-primary-600 text-textLight" />
							<span className="text-sm font-medium text-textDark">
								Loading document…
							</span>
						</div>
					) : (
						document?.blob && (
							<PdfViewer file={document.blob} className="w-full" />
						)
					)}
				</div>
				{view === "document" && (
					<Button
						type="button"
						className="w-full mt-4"
						onClick={() => {
							setIsDrawerOpen(true);
						}}
						disabled={!document?.blob}
						data-testid="agreement-document-continue"
					>
						Continue to sign
					</Button>
				)}
				<Drawer
					open={isDrawerOpen}
					onOpenChange={(open) => {
						if (open) {
							setIsDrawerOpen(true);
						} else if (!signSucceeded) {
							setIsDrawerOpen(false);
						}
					}}
				>
					<DrawerContent className="flex flex-col max-w-3xl mx-auto">
						<PageLoader
							query={documentQuery}
							customErrorUI={() => {
								return (
									<div className="text-textDark text-lg text-center py-8">
										{static_errors.unexpected_contact_support}
									</div>
								);
							}}
						>
							{(document) => {
								return (
									<UserAgreementSigning
										onContinueClick={() => {
											router.invalidate();
											setView("main");
											setIsDrawerOpen(false);
										}}
										isSuccess={signSucceeded}
										locationId={locationId}
										documentId={document.id}
										onSubmitSigning={(payload) => {
											signDocumentMutation.mutate(payload);
										}}
										isSubmitting={signDocumentMutation.isPending}
									/>
								);
							}}
						</PageLoader>
					</DrawerContent>
				</Drawer>
			</div>
		);
	}

	return (
		<div>
			<div className="p-6 flex flex-col gap-4 w-full h-dvh max-h-dvh">
				<div className="">
					<div className="flex flex-col items-center">
						<Typography variant="h1" className="text-center">
							Review and Sign <br /> User Agreement
						</Typography>
					</div>
					<div className="flex-1 flex flex-col items-center justify-between pb-6">
						<Typography variant="p" className="mt-4 text-center items-center">
							To protect you and comply with financial regulations, we need you
							to review and sign our User Agreement. This is a one-time
							requirement.
						</Typography>
					</div>
				</div>
				<div className="flex flex-grow flex-col justify-end  space-y-4">
					<Link
						to="/main/home"
						className={buttonVariants({ variant: "link", className: "w-full" })}
					>
						Back
					</Link>
					<Button
						type="button"
						className="w-full mt-4 flex-shrink-0"
						onClick={() => {
							trackEvent(eventTypes.document_opened);
							setView("document");
						}}
						data-testid="sign-now-button"
					>
						View Agreement
					</Button>
				</div>
			</div>
		</div>
	);
}
